import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { Button } from '../components/ui/button.js';
import {
  REPORT_DATE_PRESETS,
  reportPresetWindow,
  type ReportDatePreset,
} from '../lib/report-date-presets.js';

/**
 * SCRN 56 — Admin Console (CCF-113 / #302).
 *
 * A new top-level `/admin` surface, accessible ONLY to the Admin and
 * Supervisor roles (deny-by-default RBAC — see `canViewScreen` in App.tsx,
 * which renders `PermissionDeniedPage` for every other role; the server-side
 * screen + API grants in `rbac/data/permission-matrix.ts` are the
 * authoritative enforcement).
 *
 * FIRST INCREMENT (dylan's #302 comment): "Admins can access all features but
 * supervisors can only access user and Teller ID management." The only
 * features that exist in this increment ARE User Management + Teller ID
 * Management, so both roles currently see the same two tabs — Admin's "all
 * features" and Supervisor's narrower grant are enforced identically today
 * and will diverge automatically as further Admin-only areas are added (CCF-
 * 113 also describes Role Permission Management + Scripting Text Management,
 * both deferred — see the #302 status comment for what remains).
 *
 * "Teller ID Management" is the CRUD surface over the per-role Teller-ID
 * registry (`GET/POST /api/admin/teller-registry*`) — a runtime-editable
 * overlay on the interim per-role sandbox directory
 * (`server/src/auth/teller-directory.ts`) — plus, below it, the per-agent
 * directory's Teller ID fields (see below).
 *
 * THIRD INCREMENT (#302 follow-up, 2026-07-09 — Dylan reopened #302 after the
 * first two increments: "The screen I am looking at does not have a list of
 * usernames at all… Where does the username sit in here? How will users be
 * created? There is also no option to manually create a user. How do I
 * change the role of a service Agent or a ECL agent to floor walker."):
 * "User Management" now renders the REAL per-agent directory
 * (`entra-provisioning.ts`'s `AgentDirectoryEntry` list, `GET
 * /api/admin/agent-directory`) — one row per provisioned username/email +
 * role + Teller-ID status — and, per row, the CCF-113 §2 Agent→Floor-Support
 * override (**Promote to Floor Support** / **Remove override**) — the ONE
 * role transition the portal permits ("no other role transitions are
 * permitted via the portal… any other role change must go through Entra
 * ID"). The per-role Teller-ID table that used to be duplicated onto this tab
 * has moved to live ONLY on Teller ID Management, where it belongs.
 *
 * FOURTH INCREMENT (#821, @dylan219 2026-09-21 — "completely remove the
 * ability to create new users in the admin portal for ANYONE — we'll rely on
 * JIT provisioning"): the manual **Create user** form/action has been removed
 * for every role including Admin. Users are provisioned exclusively via Entra
 * JIT provisioning; the `POST /admin/agent-directory/create` route + contract
 * path are retained but are now permanently deny-for-all (its RBAC grant was
 * removed, so deny-by-default 403s it for everyone).
 *
 * SECOND INCREMENT (#302, JIT Entra provisioning): under `AUTH_MODE=entra`,
 * an agent's login JIT-creates/re-syncs their directory entry (see
 * `entra-provisioning.ts`). That entry is exactly what the third increment
 * above now surfaces — this directory is no longer an Entra-only, additive
 * curiosity; it is the single source of truth for "User Management".
 */

interface TellerRegistryEntry {
  role: string;
  tellerId?: string;
  manageable: boolean;
  updatedAt: string;
  updatedBy?: string;
}

interface AgentDirectoryEntry {
  identity: string;
  role: string;
  tellerId?: string | null;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
  /** Set only while `role === 'FloorSupport'` via the CCF-113 §2 override —
   * see `entra-provisioning.ts`'s `AgentDirectoryEntry.overriddenFrom`. */
  overriddenFrom?: string | null;
  /** ISO instant the Floor-Support override auto-reverts (11pm same day, #594). */
  floorSupportExpiresAt?: string | null;
}

/**
 * Bank-holiday data for the "Stop and Think" clock (#460, CCF-11 increment 2).
 * `effective` is the merged set the clock uses (seeded ∪ added − removed);
 * `added`/`removed` are the raw admin override lists.
 */
interface EffectiveBankHoliday {
  date: string;
  name: string;
  source: 'seeded' | 'added';
}
interface BankHolidayData {
  effective: EffectiveBankHoliday[];
  added: { date: string; name: string }[];
  removed: string[];
}

/** One editable content-catalogue item (#637, CCF-113, SCRN-56). */
interface ContentItem {
  key: string;
  screenId: string;
  label: string;
  default: string;
  maxLength: number;
  value: string;
  overridden: boolean;
  updatedAt: string;
  updatedBy: string;
  /**
   * #637 — compliance-sensitive regulatory scripting (e.g. the mandatory
   * registration fraud-warning script). When true the UI shows a
   * "Regulatory — compliance-sensitive, every edit is audited" note. Editing
   * stays via the same Admin-only routes; the flag is presentational only.
   */
  regulatory?: boolean;
}

type Tab =
  | 'users'
  | 'tellerId'
  | 'bankHolidays'
  | 'rbacMatrix'
  | 'content'
  | 'reporting';

/**
 * Web App Reporting (#668, CCF-113, SCRN-56) — one function's Attempted /
 * Success / Failure tally, optionally scoped to a UTC hour (Hourly granularity).
 * Mirrors the server's `ActionVolumeRow` (the generated contract type).
 */
interface ActionVolumeRow {
  eventName: string;
  hour?: string;
  attempted: number;
  success: number;
  failure: number;
  /**
   * #686 — events for a business function with NO pass/fail concept (an
   * audit-trail / occurrence record such as "Caller Identified", "Stop and
   * Think Advisory", "Cleared Down Session"). Counted here rather than as
   * failures so they are not mis-shown as "failed". Absent on older payloads
   * ⇒ treated as 0.
   */
  informational?: number;
}
interface ActionVolumeReportData {
  from: string;
  to: string;
  granularity: 'total' | 'hourly';
  totalEvents: number;
  rows: ActionVolumeRow[];
}

/**
 * #668 — build the CSV for the on-screen report client-side (opens directly in
 * Excel; no new dependency, keeps the supply-chain gates clean). One header row
 * + one row per function (Total) or per function × UTC-hour (Hourly). Mirrors
 * the server's `reportToCsv` so a future server `?format=csv` stays consistent.
 */
function csvEscape(v: string | number): string {
  const s = String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function reportToCsv(report: ActionVolumeReportData): string {
  const header =
    report.granularity === 'hourly'
      ? [
          'Hour (UTC)',
          'Function',
          'Attempted',
          'Success',
          'Failure',
          'Informational',
        ]
      : ['Function', 'Attempted', 'Success', 'Failure', 'Informational'];
  const lines = [header.map(csvEscape).join(',')];
  for (const r of report.rows) {
    const cells =
      report.granularity === 'hourly'
        ? [
            r.hour ?? '',
            r.eventName,
            r.attempted,
            r.success,
            r.failure,
            r.informational ?? 0,
          ]
        : [
            r.eventName,
            r.attempted,
            r.success,
            r.failure,
            r.informational ?? 0,
          ];
    lines.push(cells.map(csvEscape).join(','));
  }
  return lines.join('\r\n');
}

/**
 * Web App Reporting increment 2 (#668, Req 2) — the per-agent breakdown. One
 * agent × function tally (× UTC hour when hourly). Mirrors the server's
 * `AgentVolumeRow` / `AgentVolumeReport` (the generated contract types).
 */
type AgentIdentifier = 'agentUpn' | 'tellerId';

/**
 * #686 — sentinel value for the per-agent picker's "All agents" option. Chosen
 * so it can't collide with a real agent UPN or 4-digit Teller ID.
 */
const ALL_AGENTS = '__all__';

interface AgentVolumeRow {
  agent: string;
  eventName: string;
  hour?: string;
  attempted: number;
  success: number;
  failure: number;
  /** #686 — see {@link ActionVolumeRow.informational}. Absent ⇒ 0. */
  informational?: number;
}
interface AgentVolumeReportData {
  from: string;
  to: string;
  granularity: 'total' | 'hourly';
  by: AgentIdentifier;
  totalEvents: number;
  rows: AgentVolumeRow[];
}

/**
 * #668 increment 2 — build the per-agent CSV client-side (mirrors the server's
 * `agentReportToCsv`; opens directly in Excel, no new dependency). The
 * agent-identifier column header reflects the chosen `by`.
 */
function agentReportToCsv(report: AgentVolumeReportData): string {
  const agentHeader = report.by === 'tellerId' ? 'Teller ID' : 'Agent UPN';
  const header =
    report.granularity === 'hourly'
      ? [
          'Hour (UTC)',
          agentHeader,
          'Function',
          'Attempted',
          'Success',
          'Failure',
          'Informational',
        ]
      : [
          agentHeader,
          'Function',
          'Attempted',
          'Success',
          'Failure',
          'Informational',
        ];
  const lines = [header.map(csvEscape).join(',')];
  for (const r of report.rows) {
    const cells =
      report.granularity === 'hourly'
        ? [
            r.hour ?? '',
            r.agent,
            r.eventName,
            r.attempted,
            r.success,
            r.failure,
            r.informational ?? 0,
          ]
        : [
            r.agent,
            r.eventName,
            r.attempted,
            r.success,
            r.failure,
            r.informational ?? 0,
          ];
    lines.push(cells.map(csvEscape).join(','));
  }
  return lines.join('\r\n');
}

/**
 * RBAC Permissions tab (#584, CCF-113) — the effective permission matrix
 * (`GET /api/admin/rbac-matrix`): every role with a Yes/No (Online + Offline)
 * for each screen + API-action grant. Every cell (including a denied No/No
 * cell the role does not yet hold, #847) is
 * editable for an ADMIN principal (each Yes/No is a toggle that calls
 * `POST /api/admin/rbac-matrix/grant`, optimistic update + refetch, inline
 * error on failure); every other role — including Supervisor — keeps the
 * read-only view. Creating a new role is out of scope here.
 */
interface RbacMatrixPermission {
  kind: 'screen' | 'api';
  id: string;
  label: string;
  group: string;
  online: boolean;
  offline: boolean;
}
interface RbacMatrixRole {
  role: string;
  permissions: RbacMatrixPermission[];
}
interface RbacMatrixData {
  version: string;
  roles: RbacMatrixRole[];
}

const ROLE_LABELS: Record<string, string> = {
  ServiceAgent: 'Service Agent',
  ECLAgent: 'ECL Agent',
  FloorSupport: 'Floor Support',
  Supervisor: 'Supervisor',
  SalesAgent: 'Sales Agent',
  Admin: 'Admin',
  // #647 (@dylan219): the "Support Chat" role — its CANONICAL identifier was
  // renamed from `Audit` to `SupportChat` everywhere in the app (legacy
  // `Audit` claim values are normalized server-side at the identity seam, so
  // PTSB's Entra/Cognito groups did not need to change).
  SupportChat: 'Support Chat',
  QA: 'QA',
};

/**
 * The only roles CCF-113 §2 lets the portal promote to Floor Support —
 * mirrors the server's authoritative `FLOORWALKER_EXEMPT_ROLES`
 * (`server/src/auth/entra-provisioning.ts`); the server rejects anything
 * else regardless of what this UI shows, this just avoids offering a button
 * that would 400.
 */
const FLOOR_SUPPORT_PROMOTABLE_ROLES = new Set([
  'ServiceAgent',
  'ECLAgent',
  'SalesAgent',
]);

function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

/**
 * #594 — a Floor-Support override auto-reverts at 11pm (Europe/Dublin) the day
 * it was applied. Render its `floorSupportExpiresAt` instant as a short Dublin
 * wall-clock date+time so an Admin can see when the promotion lapses. Returns
 * empty string for a missing/unparseable value (older overrides carry none).
 */
function floorSupportExpiryLabel(iso?: string | null): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  return new Intl.DateTimeFormat('en-IE', {
    timeZone: 'Europe/Dublin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(t));
}

/**
 * #589 (@dylan219) — the User Management and Teller ID agent lists get a
 * search box so an agent can be found without scrolling the full directory.
 * Filtering only activates once the query reaches MIN_SEARCH_CHARS characters
 * ("make it a minimum of 3 characters to initiate the search"); below that the
 * full list is shown. Matches username/email or role label, case-insensitive.
 */
const MIN_SEARCH_CHARS = 3;

function filterAgentEntries(
  entries: AgentDirectoryEntry[],
  query: string,
): AgentDirectoryEntry[] {
  const q = query.trim().toLowerCase();
  if (q.length < MIN_SEARCH_CHARS) return entries;
  return entries.filter(
    (e) =>
      e.identity.toLowerCase().includes(q) ||
      roleLabel(e.role).toLowerCase().includes(q),
  );
}

export function AdminPortalScreen(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>('users');
  const [entries, setEntries] = useState<TellerRegistryEntry[]>([]);
  const [agentEntries, setAgentEntries] = useState<AgentDirectoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Draft Teller-ID input per role, keyed by role id, for the Teller ID
  // Management tab's editable fields.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingRole, setSavingRole] = useState<string | null>(null);
  // Same, keyed by agent identity, for the per-agent (Entra JIT) section.
  const [agentDrafts, setAgentDrafts] = useState<Record<string, string>>({});
  const [savingAgent, setSavingAgent] = useState<string | null>(null);
  // Per-row Floor Support promote/remove-override action in flight.
  const [roleActionFor, setRoleActionFor] = useState<string | null>(null);
  // #589 — independent agent-search queries for the User Management tab and the
  // Teller ID Management tab's per-agent list. Each filters at MIN_SEARCH_CHARS.
  const [userSearch, setUserSearch] = useState('');
  const [tellerSearch, setTellerSearch] = useState('');

  // Bank Holidays tab (#460, CCF-11 increment 2) — the "Stop and Think" clock's
  // admin bank-holiday override layer. Inline success/error beside the action
  // (the #80/#82/#94 rule), not a toast.
  const [holidays, setHolidays] = useState<BankHolidayData | null>(null);
  const [newHolidayDate, setNewHolidayDate] = useState('');
  const [newHolidayName, setNewHolidayName] = useState('');
  const [holidayBusy, setHolidayBusy] = useState(false);
  const [holidaySuccess, setHolidaySuccess] = useState('');
  const [holidayError, setHolidayError] = useState('');

  // RBAC Permissions tab (#584) — the effective permission matrix + the role
  // currently selected for display.
  const [rbacMatrix, setRbacMatrix] = useState<RbacMatrixData | null>(null);
  const [selectedRbacRole, setSelectedRbacRole] = useState<string>('');
  // #584 increment 2a — the signed-in principal's role. Editing the RBAC matrix
  // is Admin only; every other role (incl. Supervisor) keeps the read-only
  // view. Best-effort: absence ⇒ read-only (fail-safe).
  const [principalRole, setPrincipalRole] = useState<string>('');
  // The grant cell currently being saved (`${role}:${kind}:${id}:${state}`), for
  // the busy affordance, plus an inline error for the RBAC editor.
  const [savingGrant, setSavingGrant] = useState<string | null>(null);
  const [rbacError, setRbacError] = useState('');

  // Scripting & guidance (content catalogue) tab (#637, CCF-113, SCRN-56) — the
  // admin-editable agent-facing scripting text. Inline success/error beside the
  // action (the #80/#82/#94 rule), not a toast. `drafts` holds the in-progress
  // textarea edit per key; the busy key marks the item being saved/reset.
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [contentDrafts, setContentDrafts] = useState<Record<string, string>>(
    {},
  );
  const [contentBusyKey, setContentBusyKey] = useState<string | null>(null);
  const [contentSuccess, setContentSuccess] = useState('');
  const [contentError, setContentError] = useState('');

  // Reporting tab (#668, CCF-113, SCRN-56) — Web App Reporting Req 1: the
  // total-volume action report over a UTC window. Admin only. Defaults to the
  // last 24 hours (UTC) so the tab shows something useful on open; the admin
  // then narrows/widens the window (up to 31 days) and picks the granularity.
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  // #855 item 3 — the date-range quick pick ('' = a custom range from the
  // calendars). Picking a preset fills BOTH calendars; editing either calendar
  // afterwards drops back to Custom.
  const [reportPreset, setReportPreset] = useState<ReportDatePreset | ''>('');
  const [reportGranularity, setReportGranularity] = useState<
    'total' | 'hourly'
  >('total');
  const [report, setReport] = useState<ActionVolumeReportData | null>(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportError, setReportError] = useState('');
  // #668 increment 2 (Req 2) — the per-agent breakdown. A mode toggle between
  // the org-wide total (increment 1) and the per-agent view, plus the
  // Agent-UPN / Teller-ID identifier toggle @dylan219 asked for.
  const [reportMode, setReportMode] = useState<'total' | 'agent'>('total');
  const [reportBy, setReportBy] = useState<AgentIdentifier>('agentUpn');
  const [agentReport, setAgentReport] = useState<AgentVolumeReportData | null>(
    null,
  );
  // #686 (@dylan219) — the per-agent picker. Once a per-agent report is loaded
  // the admin can narrow the displayed + exported rows to a single agent, or
  // keep "All agents" (the default). Populated from the DISTINCT agent values
  // in the loaded report (no new BFF call — the report already carries every
  // agent's rows). `ALL_AGENTS` is the sentinel for the "show everyone" option.
  const [selectedAgent, setSelectedAgent] = useState<string>(ALL_AGENTS);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.GET('/admin/teller-registry');
      if (apiError || !data) {
        setError(
          response.status === 403
            ? 'Forbidden: check your permissions.'
            : `Could not load the Teller ID registry (${response.status}).`,
        );
        return;
      }
      const rows = (data.entries ?? []) as unknown as TellerRegistryEntry[];
      setEntries(rows);
      setDrafts(
        Object.fromEntries(rows.map((e) => [e.role, e.tellerId ?? ''])),
      );

      // Per-agent (Entra JIT) directory — additive, best-effort: an issue
      // loading it should not blank out the per-role view above, which every
      // auth mode depends on.
      try {
        const agentRes = await api.GET('/admin/agent-directory');
        const agentRows = (agentRes.data?.entries ??
          []) as unknown as AgentDirectoryEntry[];
        setAgentEntries(agentRows);
        setAgentDrafts(
          Object.fromEntries(
            agentRows.map((e) => [e.identity, e.tellerId ?? '']),
          ),
        );
      } catch {
        setAgentEntries([]);
      }

      // Bank Holidays (#460) — additive, best-effort like the agent directory:
      // a load failure here must not blank out the Teller-ID view above.
      try {
        const bhRes = await api.GET('/admin/bank-holidays');
        setHolidays((bhRes.data as unknown as BankHolidayData) ?? null);
      } catch {
        setHolidays(null);
      }

      // RBAC Permissions (#584) — additive, best-effort like the sections above:
      // a load failure here must not blank out the Teller-ID view.
      try {
        const rbacRes = await api.GET('/admin/rbac-matrix');
        const matrix = (rbacRes.data as unknown as RbacMatrixData) ?? null;
        setRbacMatrix(matrix);
        if (matrix?.roles.length) {
          setSelectedRbacRole((prev) =>
            prev && matrix.roles.some((r) => r.role === prev)
              ? prev
              : matrix.roles[0]!.role,
          );
        }
      } catch {
        setRbacMatrix(null);
      }

      // Scripting & guidance (#637) — additive, best-effort like the sections
      // above: a load failure here must not blank out the Teller-ID view.
      try {
        const contentRes = await api.GET('/admin/content');
        const items =
          (contentRes.data as unknown as { items?: ContentItem[] })?.items ??
          [];
        setContentItems(items);
        setContentDrafts(
          Object.fromEntries(items.map((i) => [i.key, i.value])),
        );
      } catch {
        setContentItems([]);
      }

      // #584 increment 2a — the signed-in principal's role, to gate the RBAC
      // matrix editor (Admin only). Best-effort: on failure the editor stays
      // read-only (fail-safe). The server's RBAC gate on the grant endpoint is
      // the authoritative enforcement point regardless. (Also gates the content
      // editor — Supervisor gets a read-only view.)
      try {
        const sessionRes = await api.GET('/session');
        const role = (sessionRes.data as unknown as { role?: string })?.role;
        setPrincipalRole(typeof role === 'string' ? role : '');
      } catch {
        setPrincipalRole('');
      }
    } catch {
      setError('Request failed.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Seed the reporting window to the last 24h (UTC), as `datetime-local` values
  // (YYYY-MM-DDTHH:mm) interpreted as UTC. Runs once on mount.
  useEffect(() => {
    const now = new Date();
    const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const toLocalInput = (d: Date): string => d.toISOString().slice(0, 16);
    setReportFrom(toLocalInput(dayAgo));
    setReportTo(toLocalInput(now));
  }, []);

  async function saveTellerId(role: string): Promise<void> {
    const tellerId = (drafts[role] ?? '').trim();
    if (!/^\d{4}$/.test(tellerId)) {
      toast.error('Teller ID must be exactly 4 digits.');
      return;
    }
    setSavingRole(role);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/admin/teller-registry/upsert', {
        body: { role, tellerId } as never,
      });
      if (apiError || !data) {
        const body = apiError as { reason?: string } | undefined;
        toast.error(body?.reason || `Could not save (${response.status}).`);
        return;
      }
      toast.success(`Teller ID for ${roleLabel(role)} updated.`);
      await load();
    } catch {
      toast.error('Request failed.');
    } finally {
      setSavingRole(null);
    }
  }

  async function removeTellerId(role: string): Promise<void> {
    setSavingRole(role);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/admin/teller-registry/remove', {
        body: { role } as never,
      });
      if (apiError || !data) {
        const body = apiError as { reason?: string } | undefined;
        toast.error(body?.reason || `Could not remove (${response.status}).`);
        return;
      }
      toast.success(`Teller ID for ${roleLabel(role)} removed.`);
      await load();
    } catch {
      toast.error('Request failed.');
    } finally {
      setSavingRole(null);
    }
  }

  /**
   * #584 increment 2a — toggle an existing RBAC matrix cell's Online or Offline
   * allow flag for the given role (Admin only). Optimistic: flip the local
   * matrix immediately, persist via the grant endpoint, then refetch on success;
   * on failure surface an inline error and reload to discard the optimistic
   * change. The `state` parameter selects which flag the toggle drives.
   */
  async function toggleGrant(
    role: string,
    perm: RbacMatrixPermission,
    state: 'online' | 'offline',
  ): Promise<void> {
    const key = `${role}:${perm.kind}:${perm.id}:${state}`;
    const nextOnline = state === 'online' ? !perm.online : perm.online;
    const nextOffline = state === 'offline' ? !perm.offline : perm.offline;
    setRbacError('');
    setSavingGrant(key);
    // Optimistic local flip so the toggle responds instantly.
    setRbacMatrix((prev) =>
      prev
        ? {
            ...prev,
            roles: prev.roles.map((r) =>
              r.role !== role
                ? r
                : {
                    ...r,
                    permissions: r.permissions.map((p) =>
                      p.kind === perm.kind && p.id === perm.id
                        ? { ...p, online: nextOnline, offline: nextOffline }
                        : p,
                    ),
                  },
            ),
          }
        : prev,
    );
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/admin/rbac-matrix/grant', {
        body: {
          role,
          kind: perm.kind,
          id: perm.id,
          online: nextOnline,
          offline: nextOffline,
        } as never,
      });
      if (apiError || !data) {
        const body = apiError as { reason?: string } | undefined;
        setRbacError(
          body?.reason ||
            `Could not update the permission (${response.status}).`,
        );
      }
    } catch {
      setRbacError('Request failed.');
    } finally {
      setSavingGrant(null);
      // Refetch to reconcile the optimistic change with the server's truth
      // (success confirms it; failure discards it).
      await load();
    }
  }

  async function saveAgentTellerId(identity: string): Promise<void> {
    const tellerId = (agentDrafts[identity] ?? '').trim();
    if (!/^\d{4}$/.test(tellerId)) {
      toast.error('Teller ID must be exactly 4 digits.');
      return;
    }
    setSavingAgent(identity);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/admin/agent-directory/teller/upsert', {
        body: { identity, tellerId } as never,
      });
      if (apiError || !data) {
        const body = apiError as { reason?: string } | undefined;
        toast.error(body?.reason || `Could not save (${response.status}).`);
        return;
      }
      toast.success(`Teller ID for ${identity} updated.`);
      await load();
    } catch {
      toast.error('Request failed.');
    } finally {
      setSavingAgent(null);
    }
  }

  async function removeAgentTellerId(identity: string): Promise<void> {
    setSavingAgent(identity);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/admin/agent-directory/teller/remove', {
        body: { identity } as never,
      });
      if (apiError || !data) {
        const body = apiError as { reason?: string } | undefined;
        toast.error(body?.reason || `Could not remove (${response.status}).`);
        return;
      }
      toast.success(`Teller ID for ${identity} removed.`);
      await load();
    } catch {
      toast.error('Request failed.');
    } finally {
      setSavingAgent(null);
    }
  }

  async function promoteToFloorSupport(identity: string): Promise<void> {
    setRoleActionFor(identity);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/admin/agent-directory/promote-floor-support', {
        body: { identity } as never,
      });
      if (apiError || !data) {
        const body = apiError as { reason?: string } | undefined;
        toast.error(body?.reason || `Could not promote (${response.status}).`);
        return;
      }
      toast.success(`${identity} is now Floor Support.`);
      await load();
    } catch {
      toast.error('Request failed.');
    } finally {
      setRoleActionFor(null);
    }
  }

  async function removeFloorSupportOverride(identity: string): Promise<void> {
    setRoleActionFor(identity);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST(
        '/admin/agent-directory/remove-floor-support-override',
        { body: { identity } as never },
      );
      if (apiError || !data) {
        const body = apiError as { reason?: string } | undefined;
        toast.error(body?.reason || `Could not remove (${response.status}).`);
        return;
      }
      toast.success(`Floor Support override removed for ${identity}.`);
      await load();
    } catch {
      toast.error('Request failed.');
    } finally {
      setRoleActionFor(null);
    }
  }

  /** Refreshes just the bank-holiday data (after an add/remove/restore). */
  async function reloadHolidays(): Promise<void> {
    try {
      const bhRes = await api.GET('/admin/bank-holidays');
      setHolidays((bhRes.data as unknown as BankHolidayData) ?? null);
    } catch {
      /* leave the current view; the inline error already reported the failure */
    }
  }

  /** Refreshes just the content-catalogue data (after a save/reset). */
  async function reloadContent(): Promise<void> {
    try {
      const res = await api.GET('/admin/content');
      const items =
        (res.data as unknown as { items?: ContentItem[] })?.items ?? [];
      setContentItems(items);
      setContentDrafts(Object.fromEntries(items.map((i) => [i.key, i.value])));
    } catch {
      /* leave the current view; the inline error already reported the failure */
    }
  }

  // #637 — save one catalogue key's override (Admin only; the server RBAC gate
  // is authoritative). Inline success/error beside the item (#80/#82/#94).
  async function saveContent(key: string): Promise<void> {
    setContentSuccess('');
    setContentError('');
    const value = (contentDrafts[key] ?? '').trim();
    if (!value) {
      setContentError(
        'The text cannot be empty. Use Reset to restore default.',
      );
      return;
    }
    setContentBusyKey(key);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/admin/content', {
        body: { key, value } as never,
      });
      if (apiError || !data) {
        const body = apiError as { reason?: string } | undefined;
        setContentError(body?.reason || `Could not save (${response.status}).`);
        return;
      }
      await reloadContent();
      setContentSuccess(`Saved "${key}".`);
    } catch {
      setContentError('Request failed.');
    } finally {
      setContentBusyKey(null);
    }
  }

  // #637 — revert one catalogue key to its baked-in default.
  async function resetContent(key: string): Promise<void> {
    setContentSuccess('');
    setContentError('');
    setContentBusyKey(key);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/admin/content/reset', {
        body: { key } as never,
      });
      if (apiError || !data) {
        const body = apiError as { reason?: string } | undefined;
        setContentError(
          body?.reason || `Could not reset (${response.status}).`,
        );
        return;
      }
      await reloadContent();
      setContentSuccess(`Reset "${key}" to default.`);
    } catch {
      setContentError('Request failed.');
    } finally {
      setContentBusyKey(null);
    }
  }

  // #668 — the `datetime-local` value (YYYY-MM-DDTHH:mm) is treated as a UTC
  // wall-clock time (UTC is the agreed reporting timezone, @dylan219 #668), so
  // we append seconds + `Z` to make an ISO-8601 UTC instant.
  function windowInputToIso(value: string): string {
    // Accept both `YYYY-MM-DDTHH:mm` and `...:ss`.
    const withSeconds = value.length === 16 ? `${value}:00` : value;
    return `${withSeconds}Z`;
  }

  async function runReport(): Promise<void> {
    setReportError('');
    if (!reportFrom || !reportTo) {
      setReportError('Enter both a start and end date/time.');
      return;
    }
    const fromIso = windowInputToIso(reportFrom);
    const toIso = windowInputToIso(reportTo);
    if (Date.parse(toIso) <= Date.parse(fromIso)) {
      setReportError('The end must be after the start.');
      return;
    }
    setReportBusy(true);
    try {
      const handleErr = (apiError: unknown, status: number): void => {
        const body = apiError as { reason?: string } | undefined;
        setReportError(
          body?.reason ||
            (status === 403
              ? 'Forbidden: reporting is available to Admins only.'
              : `Could not run the report (${status}).`),
        );
      };
      if (reportMode === 'agent') {
        const {
          data,
          error: apiError,
          response,
        } = await api.GET('/admin/reporting/agent-volume', {
          params: {
            query: {
              from: fromIso,
              to: toIso,
              granularity: reportGranularity,
              by: reportBy,
            },
          },
        });
        if (apiError || !data) {
          handleErr(apiError, response.status);
          setAgentReport(null);
          return;
        }
        setAgentReport(data as unknown as AgentVolumeReportData);
        // #686 — a fresh report may cover a different set of agents, so reset
        // the picker back to "All agents" rather than keeping a now-stale
        // single-agent selection.
        setSelectedAgent(ALL_AGENTS);
      } else {
        const {
          data,
          error: apiError,
          response,
        } = await api.GET('/admin/reporting/action-volume', {
          params: {
            query: {
              from: fromIso,
              to: toIso,
              granularity: reportGranularity,
            },
          },
        });
        if (apiError || !data) {
          handleErr(apiError, response.status);
          setReport(null);
          return;
        }
        setReport(data as unknown as ActionVolumeReportData);
      }
    } catch {
      setReportError('Request failed.');
      if (reportMode === 'agent') setAgentReport(null);
      else setReport(null);
    } finally {
      setReportBusy(false);
    }
  }

  function triggerCsvDownload(csv: string, filename: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function downloadReportCsv(): void {
    if (reportMode === 'agent') {
      if (!agentReport) return;
      // #686 — export exactly what is on screen: all rows, or just the
      // selected agent's (the picker filters the display + the export alike).
      const scoped: AgentVolumeReportData = {
        ...agentReport,
        rows: visibleAgentRows,
        totalEvents: visibleAgentEvents,
      };
      const agentSuffix =
        selectedAgent === ALL_AGENTS
          ? 'all'
          : // Keep the filename filesystem-safe (a UPN has an @ and dots).
            selectedAgent.replace(/[^A-Za-z0-9._-]+/g, '_');
      triggerCsvDownload(
        agentReportToCsv(scoped),
        `agent-volume-${scoped.by}-${agentSuffix}-${scoped.granularity}-${scoped.from.slice(0, 10)}.csv`,
      );
      return;
    }
    if (!report) return;
    triggerCsvDownload(
      reportToCsv(report),
      `action-volume-${report.granularity}-${report.from.slice(0, 10)}.csv`,
    );
  }

  async function addHoliday(): Promise<void> {
    setHolidaySuccess('');
    setHolidayError('');
    const date = newHolidayDate.trim();
    const name = newHolidayName.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setHolidayError('Enter a valid date (YYYY-MM-DD).');
      return;
    }
    if (!name) {
      setHolidayError('Enter a name for the bank holiday.');
      return;
    }
    setHolidayBusy(true);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/admin/bank-holidays', {
        body: { date, name } as never,
      });
      if (apiError || !data) {
        const body = apiError as { reason?: string } | undefined;
        setHolidayError(body?.reason || `Could not add (${response.status}).`);
        return;
      }
      setHolidays(data as unknown as BankHolidayData);
      setHolidaySuccess(`Added ${name} (${date}).`);
      setNewHolidayDate('');
      setNewHolidayName('');
    } catch {
      setHolidayError('Request failed.');
    } finally {
      setHolidayBusy(false);
    }
  }

  async function removeHoliday(date: string): Promise<void> {
    setHolidaySuccess('');
    setHolidayError('');
    setHolidayBusy(true);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/admin/bank-holidays/remove', {
        body: { date } as never,
      });
      if (apiError || !data) {
        const body = apiError as { reason?: string } | undefined;
        setHolidayError(
          body?.reason || `Could not remove (${response.status}).`,
        );
        await reloadHolidays();
        return;
      }
      setHolidays(data as unknown as BankHolidayData);
      setHolidaySuccess(`Removed ${date}.`);
    } catch {
      setHolidayError('Request failed.');
    } finally {
      setHolidayBusy(false);
    }
  }

  async function restoreHoliday(date: string): Promise<void> {
    setHolidaySuccess('');
    setHolidayError('');
    setHolidayBusy(true);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/admin/bank-holidays/restore', {
        body: { date } as never,
      });
      if (apiError || !data) {
        const body = apiError as { reason?: string } | undefined;
        setHolidayError(
          body?.reason || `Could not restore (${response.status}).`,
        );
        await reloadHolidays();
        return;
      }
      setHolidays(data as unknown as BankHolidayData);
      setHolidaySuccess(`Restored ${date}.`);
    } catch {
      setHolidayError('Request failed.');
    } finally {
      setHolidayBusy(false);
    }
  }

  // #589 — filtered agent lists for the two tabs (full list below 3 chars).
  const usersFiltered = filterAgentEntries(agentEntries, userSearch);
  const tellerAgentsFiltered = filterAgentEntries(agentEntries, tellerSearch);

  // #686 — the distinct agents present in the loaded per-agent report, sorted
  // for a stable dropdown order. Drives the per-agent picker's options.
  const reportAgents = agentReport
    ? [...new Set(agentReport.rows.map((r) => r.agent))].sort((a, b) =>
        a.localeCompare(b),
      )
    : [];
  // The rows actually shown/exported: all rows, or just the selected agent's.
  const visibleAgentRows =
    agentReport && selectedAgent !== ALL_AGENTS
      ? agentReport.rows.filter((r) => r.agent === selectedAgent)
      : (agentReport?.rows ?? []);
  const visibleAgentEvents = visibleAgentRows.reduce(
    (n, r) => n + r.attempted,
    0,
  );

  return (
    <main
      className="max-w-[1400px] mx-auto px-12 py-12"
      aria-labelledby="admin-console-heading"
    >
      <h1
        id="admin-console-heading"
        className="text-[24px] font-['Lato','Arial',sans-serif] font-bold text-[#1D252C] mb-6"
      >
        Admin Console
      </h1>

      <div
        role="tablist"
        aria-label="Admin Console areas"
        className="flex gap-2 mb-6 border-b-2 border-[#dad7d3]"
      >
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'users'}
          onClick={() => setTab('users')}
          className={`px-5 py-3 text-[16px] font-['Lato','Arial',sans-serif] font-bold border-b-2 -mb-[2px] ${
            tab === 'users'
              ? 'border-[#D23A06] text-[#D23A06]'
              : 'border-transparent text-[#1d252c] hover:text-[#D23A06]'
          }`}
        >
          User Management
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'tellerId'}
          onClick={() => setTab('tellerId')}
          className={`px-5 py-3 text-[16px] font-['Lato','Arial',sans-serif] font-bold border-b-2 -mb-[2px] ${
            tab === 'tellerId'
              ? 'border-[#D23A06] text-[#D23A06]'
              : 'border-transparent text-[#1d252c] hover:text-[#D23A06]'
          }`}
        >
          Teller ID Management
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'bankHolidays'}
          onClick={() => setTab('bankHolidays')}
          className={`px-5 py-3 text-[16px] font-['Lato','Arial',sans-serif] font-bold border-b-2 -mb-[2px] ${
            tab === 'bankHolidays'
              ? 'border-[#D23A06] text-[#D23A06]'
              : 'border-transparent text-[#1d252c] hover:text-[#D23A06]'
          }`}
        >
          Bank Holidays
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'rbacMatrix'}
          onClick={() => setTab('rbacMatrix')}
          className={`px-5 py-3 text-[16px] font-['Lato','Arial',sans-serif] font-bold border-b-2 -mb-[2px] ${
            tab === 'rbacMatrix'
              ? 'border-[#D23A06] text-[#D23A06]'
              : 'border-transparent text-[#1d252c] hover:text-[#D23A06]'
          }`}
        >
          RBAC Permissions
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'content'}
          onClick={() => setTab('content')}
          className={`px-5 py-3 text-[16px] font-['Lato','Arial',sans-serif] font-bold border-b-2 -mb-[2px] ${
            tab === 'content'
              ? 'border-[#D23A06] text-[#D23A06]'
              : 'border-transparent text-[#1d252c] hover:text-[#D23A06]'
          }`}
        >
          Scripting &amp; guidance
        </button>
        {/* #668 — Reporting is Admin only (getActionVolumeReport is granted to
            Admin only); Supervisor does not see this tab. The server RBAC gate
            is the authoritative enforcement. */}
        {principalRole === 'Admin' && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'reporting'}
            onClick={() => setTab('reporting')}
            className={`px-5 py-3 text-[16px] font-['Lato','Arial',sans-serif] font-bold border-b-2 -mb-[2px] ${
              tab === 'reporting'
                ? 'border-[#D23A06] text-[#D23A06]'
                : 'border-transparent text-[#1d252c] hover:text-[#D23A06]'
            }`}
          >
            Reporting
          </button>
        )}
      </div>

      {error && (
        <div
          role="alert"
          className="mb-6 p-4 bg-white border border-[#dc2626] font-['Lato','Arial',sans-serif] text-[14px] text-[#1d252c] max-w-[1200px]"
        >
          {error}
        </div>
      )}

      {loading && (
        <p className="mb-6 font-['Lato','Arial',sans-serif] text-[16px] text-[#6b7280]">
          Loading…
        </p>
      )}

      {!loading && !error && tab === 'users' && (
        <div className="max-w-[1200px]" data-testid="user-management-panel">
          {agentEntries.length === 0 ? (
            <p className="font-['Lato','Arial',sans-serif] text-[14px] text-[#6b7280]">
              No users have been provisioned yet. Users are provisioned
              automatically on their first login (Entra JIT provisioning) —
              there is no manual create-user path.
            </p>
          ) : (
            <>
              {/* #589 — agent search (filters at 3+ characters). */}
              <div className="mb-4">
                <label
                  htmlFor="user-search"
                  className="block text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] mb-1"
                >
                  Search users
                </label>
                <input
                  id="user-search"
                  type="search"
                  value={userSearch}
                  onChange={(ev) => setUserSearch(ev.target.value)}
                  placeholder="Search by username, email or role"
                  aria-label="Search users by username, email or role"
                  className="border border-[#979C9F] rounded px-2 py-1 w-80 text-[14px]"
                />
                <p className="mt-1 text-[12px] font-['Lato','Arial',sans-serif] text-[#6b7280]">
                  Enter at least 3 characters to search.
                </p>
              </div>
              {usersFiltered.length === 0 ? (
                <p
                  className="font-['Lato','Arial',sans-serif] text-[14px] text-[#6b7280]"
                  data-testid="user-management-no-match"
                >
                  No users match &quot;{userSearch.trim()}&quot;.
                </p>
              ) : (
                <div className="border-2 border-[#dad7d3] bg-white overflow-x-auto">
                  <table
                    className="w-full border-collapse"
                    role="table"
                    aria-label="Provisioned users"
                  >
                    <thead>
                      <tr className="bg-[#f3f4f6]">
                        {[
                          'Username / email',
                          'Role',
                          'Teller ID',
                          'Actions',
                        ].map((h) => (
                          <th
                            key={h}
                            scope="col"
                            className="text-left px-4 py-3 text-[14px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-r border-[#dad7d3] last:border-r-0"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {usersFiltered.map((e) => (
                        <tr key={e.identity} className="hover:bg-[#f9fafb]">
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                            {e.identity}
                          </td>
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                            {roleLabel(e.role)}
                            {e.overriddenFrom &&
                              ` (override; was ${roleLabel(e.overriddenFrom)})`}
                            {e.overriddenFrom && e.floorSupportExpiresAt && (
                              <span className="block text-[12px] text-[#6b7280]">
                                Reverts{' '}
                                {floorSupportExpiryLabel(
                                  e.floorSupportExpiresAt,
                                )}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                            {e.tellerId ?? 'Not assigned'}
                          </td>
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-[#dad7d3]">
                            {FLOOR_SUPPORT_PROMOTABLE_ROLES.has(e.role) && (
                              <Button
                                type="button"
                                size="sm"
                                disabled={roleActionFor === e.identity}
                                onClick={() =>
                                  void promoteToFloorSupport(e.identity)
                                }
                                aria-label={`Promote ${e.identity} to Floor Support`}
                              >
                                Promote to Floor Support
                              </Button>
                            )}
                            {e.role === 'FloorSupport' && e.overriddenFrom && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={roleActionFor === e.identity}
                                onClick={() =>
                                  void removeFloorSupportOverride(e.identity)
                                }
                                aria-label={`Remove Floor Support override for ${e.identity}`}
                              >
                                Remove override
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!loading && !error && tab === 'tellerId' && (
        <div
          className="border-2 border-[#dad7d3] bg-white max-w-[1200px] overflow-x-auto"
          data-testid="teller-id-management-panel"
        >
          <table
            className="w-full border-collapse"
            role="table"
            aria-label="Teller ID registry"
          >
            <thead>
              <tr className="bg-[#f3f4f6]">
                {['Role', 'Teller ID', ''].map((h, i) => (
                  <th
                    key={`${h}-${i}`}
                    scope="col"
                    className="text-left px-4 py-3 text-[14px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-r border-[#dad7d3] last:border-r-0"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.role} className="hover:bg-[#f9fafb]">
                  <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                    {roleLabel(e.role)}
                  </td>
                  <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                    {e.manageable ? (
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={4}
                        aria-label={`Teller ID for ${roleLabel(e.role)}`}
                        value={drafts[e.role] ?? ''}
                        onChange={(ev) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [e.role]: ev.target.value.replace(/\D/g, ''),
                          }))
                        }
                        className="border border-[#979C9F] rounded px-2 py-1 w-24 text-[14px]"
                      />
                    ) : (
                      <span className="text-[#6b7280]">
                        Not applicable (no banking calls)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-[#dad7d3]">
                    {e.manageable && (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          disabled={savingRole === e.role}
                          onClick={() => void saveTellerId(e.role)}
                          aria-label={`Save Teller ID for ${roleLabel(e.role)}`}
                        >
                          Save
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={savingRole === e.role || !e.tellerId}
                          onClick={() => void removeTellerId(e.role)}
                          aria-label={`Remove Teller ID for ${roleLabel(e.role)}`}
                        >
                          Remove
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && !error && tab === 'tellerId' && agentEntries.length > 0 && (
        <div
          className="mt-8 max-w-[1200px]"
          data-testid="agent-directory-teller-panel"
        >
          <h2 className="text-[16px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] mb-2">
            Provisioned users
          </h2>
          {/* #589 — agent search (filters at 3+ characters). */}
          <div className="mb-4">
            <label
              htmlFor="teller-agent-search"
              className="block text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] mb-1"
            >
              Search users
            </label>
            <input
              id="teller-agent-search"
              type="search"
              value={tellerSearch}
              onChange={(ev) => setTellerSearch(ev.target.value)}
              placeholder="Search by username, email or role"
              aria-label="Search users by username, email or role"
              className="border border-[#979C9F] rounded px-2 py-1 w-80 text-[14px]"
            />
            <p className="mt-1 text-[12px] font-['Lato','Arial',sans-serif] text-[#6b7280]">
              Enter at least 3 characters to search.
            </p>
          </div>
          {tellerAgentsFiltered.length === 0 ? (
            <p
              className="font-['Lato','Arial',sans-serif] text-[14px] text-[#6b7280]"
              data-testid="agent-directory-teller-no-match"
            >
              No users match &quot;{tellerSearch.trim()}&quot;.
            </p>
          ) : (
            <div className="border-2 border-[#dad7d3] bg-white overflow-x-auto">
              <table
                className="w-full border-collapse"
                role="table"
                aria-label="Provisioned users Teller IDs"
              >
                <thead>
                  <tr className="bg-[#f3f4f6]">
                    {['Agent', 'Role', 'Teller ID', ''].map((h, i) => (
                      <th
                        key={`${h}-${i}`}
                        scope="col"
                        className="text-left px-4 py-3 text-[14px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-r border-[#dad7d3] last:border-r-0"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tellerAgentsFiltered.map((e) => (
                    <tr key={e.identity} className="hover:bg-[#f9fafb]">
                      <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                        {e.identity}
                      </td>
                      <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                        {roleLabel(e.role)}
                      </td>
                      <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={4}
                          aria-label={`Teller ID for ${e.identity}`}
                          value={agentDrafts[e.identity] ?? ''}
                          onChange={(ev) =>
                            setAgentDrafts((prev) => ({
                              ...prev,
                              [e.identity]: ev.target.value.replace(/\D/g, ''),
                            }))
                          }
                          className="border border-[#979C9F] rounded px-2 py-1 w-24 text-[14px]"
                        />
                      </td>
                      <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-[#dad7d3]">
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            disabled={savingAgent === e.identity}
                            onClick={() => void saveAgentTellerId(e.identity)}
                            aria-label={`Save Teller ID for ${e.identity}`}
                          >
                            Save
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={savingAgent === e.identity || !e.tellerId}
                            onClick={() => void removeAgentTellerId(e.identity)}
                            aria-label={`Remove Teller ID for ${e.identity}`}
                          >
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!loading && !error && tab === 'bankHolidays' && (
        <div className="max-w-[1200px]" data-testid="bank-holidays-panel">
          <p className="mb-6 font-['Lato','Arial',sans-serif] text-[14px] text-[#6b7280] max-w-[900px]">
            Bank holidays pause the &quot;Stop and Think&quot; rolling 48-hour
            activity clock exactly like a weekend does. This list starts from
            the seeded Irish bank holidays; add an extra closure date, or remove
            (suppress) a date so it no longer pauses the clock.
          </p>

          {/* Add form + inline success/error (the #80/#82/#94 rule). */}
          <div
            className="mb-6 border-2 border-[#dad7d3] bg-white p-6"
            data-testid="add-bank-holiday-panel"
          >
            <h2 className="text-[16px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] mb-4">
              Add a bank holiday
            </h2>
            <div className="flex gap-4 items-end flex-wrap">
              <div>
                <label
                  htmlFor="new-holiday-date"
                  className="block text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] mb-1"
                >
                  Date
                </label>
                <input
                  id="new-holiday-date"
                  type="date"
                  value={newHolidayDate}
                  onChange={(ev) => setNewHolidayDate(ev.target.value)}
                  className="border border-[#979C9F] rounded px-2 py-1 text-[14px]"
                />
              </div>
              <div>
                <label
                  htmlFor="new-holiday-name"
                  className="block text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] mb-1"
                >
                  Name
                </label>
                <input
                  id="new-holiday-name"
                  type="text"
                  value={newHolidayName}
                  onChange={(ev) => setNewHolidayName(ev.target.value)}
                  className="border border-[#979C9F] rounded px-2 py-1 w-72 text-[14px]"
                />
              </div>
              <Button
                type="button"
                disabled={holidayBusy}
                onClick={() => void addHoliday()}
              >
                Add bank holiday
              </Button>
            </div>
            {holidaySuccess && (
              <p
                role="status"
                data-testid="bank-holiday-success"
                className="mt-4 font-['Lato','Arial',sans-serif] text-[14px] text-[#0f7b3f]"
              >
                {holidaySuccess}
              </p>
            )}
            {holidayError && (
              <p
                role="alert"
                data-testid="bank-holiday-error"
                className="mt-4 font-['Lato','Arial',sans-serif] text-[14px] text-[#dc2626]"
              >
                {holidayError}
              </p>
            )}
          </div>

          {/* Effective set — seeded ∪ added − removed. */}
          <div className="border-2 border-[#dad7d3] bg-white overflow-x-auto">
            <table
              className="w-full border-collapse"
              role="table"
              aria-label="Effective bank holidays"
            >
              <thead>
                <tr className="bg-[#f3f4f6]">
                  {['Date', 'Name', 'Source', 'Actions'].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      className="text-left px-4 py-3 text-[14px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-r border-[#dad7d3] last:border-r-0"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(holidays?.effective ?? []).map((h) => (
                  <tr key={h.date} className="hover:bg-[#f9fafb]">
                    <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                      {h.date}
                    </td>
                    <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                      {h.name}
                    </td>
                    <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                      {h.source === 'added' ? 'Admin-added' : 'Seeded'}
                    </td>
                    <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-[#dad7d3]">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={holidayBusy}
                        onClick={() => void removeHoliday(h.date)}
                        aria-label={`Remove bank holiday ${h.date}`}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Suppressed seeded dates — offer a restore. */}
          {holidays && holidays.removed.length > 0 && (
            <div className="mt-8" data-testid="suppressed-bank-holidays">
              <h2 className="text-[16px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] mb-2">
                Suppressed seeded dates
              </h2>
              <div className="border-2 border-[#dad7d3] bg-white overflow-x-auto">
                <table
                  className="w-full border-collapse"
                  role="table"
                  aria-label="Suppressed bank holidays"
                >
                  <thead>
                    <tr className="bg-[#f3f4f6]">
                      {['Date', 'Actions'].map((h) => (
                        <th
                          key={h}
                          scope="col"
                          className="text-left px-4 py-3 text-[14px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-r border-[#dad7d3] last:border-r-0"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {holidays.removed.map((date) => (
                      <tr key={date} className="hover:bg-[#f9fafb]">
                        <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                          {date}
                        </td>
                        <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-[#dad7d3]">
                          <Button
                            type="button"
                            size="sm"
                            disabled={holidayBusy}
                            onClick={() => void restoreHoliday(date)}
                            aria-label={`Restore bank holiday ${date}`}
                          >
                            Restore
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && !error && tab === 'rbacMatrix' && (
        <div className="max-w-[1200px]" data-testid="rbac-matrix-panel">
          <p className="mb-4 font-['Lato','Arial',sans-serif] text-[14px] text-[#6b7280]">
            The effective RBAC permission matrix for each role, showing whether
            each screen and API action is allowed on the Online and Offline
            journeys.
            {principalRole === 'Admin'
              ? ' Select a cell to toggle whether the permission is allowed; changes are audited and take effect immediately.'
              : ' This view is read-only.'}
            {rbacMatrix?.version ? ` Seed version ${rbacMatrix.version}.` : ''}
          </p>

          {rbacError && (
            <p
              role="alert"
              className="mb-4 font-['Lato','Arial',sans-serif] text-[14px] text-[#b00020]"
              data-testid="rbac-matrix-error"
            >
              {rbacError}
            </p>
          )}

          {!rbacMatrix || rbacMatrix.roles.length === 0 ? (
            <p
              className="font-['Lato','Arial',sans-serif] text-[14px] text-[#1d252c]"
              data-testid="rbac-matrix-empty"
            >
              The RBAC permission matrix is not available.
            </p>
          ) : (
            <>
              <div className="mb-6">
                <label
                  htmlFor="rbac-role-select"
                  className="block text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] mb-1"
                >
                  Role
                </label>
                <select
                  id="rbac-role-select"
                  value={selectedRbacRole}
                  onChange={(e) => setSelectedRbacRole(e.target.value)}
                  className="border-2 border-[#dad7d3] bg-white px-3 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c]"
                >
                  {rbacMatrix.roles.map((r) => (
                    <option key={r.role} value={r.role}>
                      {roleLabel(r.role)}
                    </option>
                  ))}
                </select>
              </div>

              {(() => {
                const roleData = rbacMatrix.roles.find(
                  (r) => r.role === selectedRbacRole,
                );
                if (!roleData) return null;
                return (
                  <div className="overflow-x-auto">
                    <table
                      className="w-full border-collapse"
                      data-testid="rbac-matrix-table"
                    >
                      <thead>
                        <tr>
                          {['Permission', 'Type', 'Online', 'Offline'].map(
                            (h) => (
                              <th
                                key={h}
                                scope="col"
                                className="text-left px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] bg-[#f3f2f1] border-b-2 border-[#dad7d3]"
                              >
                                {h}
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {roleData.permissions.map((p) => {
                          const canEdit = principalRole === 'Admin';
                          // #584 (ai-review HIGH — self-lockout): reaching AND
                          // operating the RBAC editor requires the whole chain of
                          // Admin grants — the Admin Console screen (SCRN-56), the
                          // read (getRbacMatrix) that loads the matrix, and the
                          // write (updateRbacMatrix) that saves. Disabling any of
                          // them would lock every Admin out with no way back, so
                          // all three are locked (the server-side invariant is the
                          // authority; this mirrors it in the UI). They can never
                          // be toggled off in either state.
                          const isProtected =
                            roleData.role === 'Admin' &&
                            ((p.kind === 'screen' && p.id === 'SCRN-56') ||
                              (p.kind === 'api' &&
                                (p.id === 'getRbacMatrix' ||
                                  p.id === 'updateRbacMatrix')));
                          const renderCell = (state: 'online' | 'offline') => {
                            const allowed =
                              state === 'online' ? p.online : p.offline;
                            if (!canEdit) return allowed ? 'Yes' : 'No';
                            if (isProtected) {
                              return (
                                <button
                                  type="button"
                                  disabled
                                  aria-pressed={allowed}
                                  title="Locked: this Admin permission is required to access and operate the RBAC editor and cannot be disabled (prevents self-lockout)."
                                  aria-label={`${p.label} ${state} — locked to prevent self-lockout.`}
                                  data-testid={`rbac-toggle-${p.kind}-${p.id}-${state}`}
                                  className="min-w-[52px] px-3 py-1 text-[13px] font-['Lato','Arial',sans-serif] border-2 border-[#008a00] bg-[#e6f4ea] text-[#006400] opacity-60 cursor-not-allowed"
                                >
                                  {allowed ? 'Yes' : 'No'} 🔒
                                </button>
                              );
                            }
                            const key = `${roleData.role}:${p.kind}:${p.id}:${state}`;
                            const busy = savingGrant === key;
                            return (
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() =>
                                  void toggleGrant(roleData.role, p, state)
                                }
                                aria-pressed={allowed}
                                aria-label={`${allowed ? 'Allowed' : 'Denied'} — ${p.label} ${state}. Toggle.`}
                                data-testid={`rbac-toggle-${p.kind}-${p.id}-${state}`}
                                className={`min-w-[52px] px-3 py-1 text-[13px] font-['Lato','Arial',sans-serif] border-2 disabled:opacity-50 ${
                                  allowed
                                    ? 'border-[#008a00] bg-[#e6f4ea] text-[#006400]'
                                    : 'border-[#dad7d3] bg-white text-[#6b7280]'
                                }`}
                              >
                                {allowed ? 'Yes' : 'No'}
                              </button>
                            );
                          };
                          return (
                            <tr
                              key={`${p.kind}:${p.id}`}
                              className="hover:bg-[#f9fafb]"
                            >
                              <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                                {p.label}
                              </td>
                              <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#6b7280] border-b border-r border-[#dad7d3]">
                                {p.kind === 'screen' ? 'Screen' : 'API'}
                              </td>
                              <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                                {renderCell('online')}
                              </td>
                              <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-[#dad7d3]">
                                {renderCell('offline')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {!loading && !error && tab === 'content' && (
        <div className="max-w-[1200px]" data-testid="content-panel">
          <p className="mb-4 font-['Lato','Arial',sans-serif] text-[14px] text-[#1d252c]">
            Edit the agent-facing scripting &amp; guidance text. Changes reach
            agents within about a minute — no deploy. Text only; line breaks are
            preserved. Use <strong>Reset</strong> to restore a default.
          </p>
          {contentSuccess && (
            <div
              role="status"
              data-testid="content-success"
              className="mb-4 p-3 bg-white border border-[#16a34a] font-['Lato','Arial',sans-serif] text-[14px] text-[#1d252c]"
            >
              {contentSuccess}
            </div>
          )}
          {contentError && (
            <div
              role="alert"
              data-testid="content-error"
              className="mb-4 p-3 bg-white border border-[#dc2626] font-['Lato','Arial',sans-serif] text-[14px] text-[#1d252c]"
            >
              {contentError}
            </div>
          )}
          {contentItems.length === 0 ? (
            <p
              data-testid="content-empty"
              className="font-['Lato','Arial',sans-serif] text-[14px] text-[#6b7280]"
            >
              No editable content items are available.
            </p>
          ) : (
            <div className="space-y-6">
              {contentItems.map((item) => {
                const canEdit = principalRole === 'Admin';
                const busy = contentBusyKey === item.key;
                const draft = contentDrafts[item.key] ?? item.value;
                return (
                  <div
                    key={item.key}
                    data-testid={`content-item-${item.key}`}
                    className="border-2 border-[#dad7d3] bg-white p-6"
                  >
                    <div className="flex items-baseline justify-between gap-4 mb-2">
                      <h2 className="text-[16px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c]">
                        {item.label}
                      </h2>
                      <span className="text-[12px] font-['Lato','Arial',sans-serif] text-[#6b7280]">
                        {item.screenId} · {item.key}
                        {item.overridden ? ' · edited' : ' · default'}
                      </span>
                    </div>
                    {item.regulatory && (
                      <p
                        data-testid={`content-regulatory-${item.key}`}
                        className="text-[12px] font-['Lato','Arial',sans-serif] font-bold text-[#8a2b06] bg-[#fdf1e7] border border-[#f0c9a8] px-2 py-1 mb-3 inline-block"
                      >
                        Regulatory — compliance-sensitive, every edit is audited
                      </p>
                    )}
                    <label className="sr-only" htmlFor={`content-${item.key}`}>
                      {item.label}
                    </label>
                    <textarea
                      id={`content-${item.key}`}
                      data-testid={`content-textarea-${item.key}`}
                      value={draft}
                      maxLength={item.maxLength}
                      rows={4}
                      disabled={!canEdit || busy}
                      onChange={(e) =>
                        setContentDrafts((prev) => ({
                          ...prev,
                          [item.key]: e.target.value,
                        }))
                      }
                      className="w-full p-3 border border-[#979C9F] font-['Arial',sans-serif] text-[14px] text-[#1d252c] disabled:bg-[#f3f4f6]"
                    />
                    <div className="flex items-center gap-3 mt-3">
                      <Button
                        type="button"
                        data-testid={`content-save-${item.key}`}
                        disabled={!canEdit || busy}
                        onClick={() => void saveContent(item.key)}
                      >
                        {busy ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        data-testid={`content-reset-${item.key}`}
                        disabled={!canEdit || busy || !item.overridden}
                        onClick={() => void resetContent(item.key)}
                      >
                        Reset to default
                      </Button>
                      {!canEdit && (
                        <span className="text-[12px] font-['Lato','Arial',sans-serif] text-[#6b7280]">
                          Read-only (Admin can edit).
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!loading && !error && tab === 'reporting' && (
        <div className="max-w-[1200px]" data-testid="reporting-panel">
          <p className="mb-4 font-['Lato','Arial',sans-serif] text-[14px] text-[#6b7280] max-w-[900px]">
            Volume of actions taken over a window, broken down by function with
            attempted, success, failure and informational counts. Some functions
            — &ldquo;Caller Identified&rdquo;, &ldquo;Stop and Think
            Advisory&rdquo;, &ldquo;Cleared Down Session&rdquo;,
            &ldquo;Duplicate Session Block&rdquo; and &ldquo;Team Lead
            Approval&rdquo;, &ldquo;2FA Non-completion&rdquo; — record that
            something happened rather than whether an action succeeded, so they
            are counted as Informational (not Failure). Choose Total volume for
            the org-wide numbers, or Per agent to break the same window down by
            each agent (identified by Agent UPN or Teller ID). The Total volume
            report lists every function an agent can perform, including those
            with no activity in the window. Pick a Date range (Today, Yesterday,
            This week, Last week, This month, Last month — weeks run Monday to
            Sunday) or set the From / To calendars; the To time is exclusive.
            All times are UTC; the window may be up to 31 days. Choose Total for
            a single tally, or Hourly for a per-UTC-hour breakdown. Reporting
            covers activity from the deploy date onward.
          </p>

          <div
            className="mb-6 border-2 border-[#dad7d3] bg-white p-6"
            data-testid="reporting-controls"
          >
            <div className="flex gap-4 items-end flex-wrap">
              <div>
                <label
                  htmlFor="report-mode"
                  className="block text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] mb-1"
                >
                  Report
                </label>
                <select
                  id="report-mode"
                  value={reportMode}
                  onChange={(ev) =>
                    setReportMode(ev.target.value as 'total' | 'agent')
                  }
                  className="border border-[#979C9F] rounded px-2 py-1.5 text-[14px]"
                >
                  <option value="total">Total volume</option>
                  <option value="agent">Per agent</option>
                </select>
              </div>
              {reportMode === 'agent' && (
                <div>
                  <label
                    htmlFor="report-by"
                    className="block text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] mb-1"
                  >
                    Report by
                  </label>
                  <select
                    id="report-by"
                    value={reportBy}
                    onChange={(ev) =>
                      setReportBy(ev.target.value as AgentIdentifier)
                    }
                    className="border border-[#979C9F] rounded px-2 py-1.5 text-[14px]"
                  >
                    <option value="agentUpn">Agent UPN</option>
                    <option value="tellerId">Teller ID</option>
                  </select>
                </div>
              )}
              {/* #686 (@dylan219) — the per-agent picker: narrow the displayed
                  + exported rows to a single agent, or All. Shown once a
                  per-agent report is loaded (its rows supply the options). */}
              {reportMode === 'agent' &&
                agentReport &&
                reportAgents.length > 0 && (
                  <div>
                    <label
                      htmlFor="report-agent"
                      className="block text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] mb-1"
                    >
                      Agent
                    </label>
                    <select
                      id="report-agent"
                      value={selectedAgent}
                      onChange={(ev) => setSelectedAgent(ev.target.value)}
                      className="border border-[#979C9F] rounded px-2 py-1.5 text-[14px]"
                      data-testid="report-agent-picker"
                    >
                      <option value={ALL_AGENTS}>All agents</option>
                      {reportAgents.map((a) => (
                        <option key={a} value={a}>
                          {a}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              <div>
                <label
                  htmlFor="report-preset"
                  className="block text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] mb-1"
                >
                  Date range
                </label>
                <select
                  id="report-preset"
                  value={reportPreset}
                  onChange={(ev) => {
                    const value = ev.target.value as ReportDatePreset | '';
                    setReportPreset(value);
                    if (value) {
                      const w = reportPresetWindow(value);
                      setReportFrom(w.from);
                      setReportTo(w.to);
                    }
                  }}
                  className="border border-[#979C9F] rounded px-2 py-1.5 text-[14px]"
                >
                  <option value="">Custom</option>
                  {REPORT_DATE_PRESETS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor="report-from"
                  className="block text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] mb-1"
                >
                  From (UTC)
                </label>
                <input
                  id="report-from"
                  type="datetime-local"
                  value={reportFrom}
                  onChange={(ev) => {
                    setReportFrom(ev.target.value);
                    setReportPreset('');
                  }}
                  className="border border-[#979C9F] rounded px-2 py-1 text-[14px]"
                />
              </div>
              <div>
                <label
                  htmlFor="report-to"
                  className="block text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] mb-1"
                >
                  To (UTC)
                </label>
                <input
                  id="report-to"
                  type="datetime-local"
                  value={reportTo}
                  onChange={(ev) => {
                    setReportTo(ev.target.value);
                    setReportPreset('');
                  }}
                  className="border border-[#979C9F] rounded px-2 py-1 text-[14px]"
                />
              </div>
              <div>
                <label
                  htmlFor="report-granularity"
                  className="block text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] mb-1"
                >
                  Granularity
                </label>
                <select
                  id="report-granularity"
                  value={reportGranularity}
                  onChange={(ev) =>
                    setReportGranularity(ev.target.value as 'total' | 'hourly')
                  }
                  className="border border-[#979C9F] rounded px-2 py-1.5 text-[14px]"
                >
                  <option value="total">Total</option>
                  <option value="hourly">Hourly</option>
                </select>
              </div>
              <Button
                type="button"
                disabled={reportBusy}
                onClick={() => void runReport()}
              >
                {reportBusy ? 'Running…' : 'Run report'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={
                  reportMode === 'agent'
                    ? !agentReport || visibleAgentRows.length === 0
                    : !report || report.rows.length === 0
                }
                onClick={() => downloadReportCsv()}
              >
                Download CSV
              </Button>
            </div>
            {reportError && (
              <p
                role="alert"
                data-testid="reporting-error"
                className="mt-4 font-['Lato','Arial',sans-serif] text-[14px] text-[#dc2626]"
              >
                {reportError}
              </p>
            )}
          </div>

          {reportMode === 'total' && report && (
            <>
              <p
                className="mb-3 font-['Lato','Arial',sans-serif] text-[14px] text-[#1d252c]"
                data-testid="reporting-summary"
              >
                {report.totalEvents} action
                {report.totalEvents === 1 ? '' : 's'} across{' '}
                {report.rows.length} row{report.rows.length === 1 ? '' : 's'}.
              </p>
              {report.rows.length === 0 ? (
                <p
                  data-testid="reporting-empty"
                  className="font-['Lato','Arial',sans-serif] text-[14px] text-[#6b7280]"
                >
                  No actions were recorded in this window.
                </p>
              ) : (
                <div className="border-2 border-[#dad7d3] bg-white overflow-x-auto">
                  <table
                    className="w-full border-collapse"
                    role="table"
                    aria-label="Action volume report"
                    data-testid="reporting-table"
                  >
                    <thead>
                      <tr className="bg-[#f3f4f6]">
                        {(report.granularity === 'hourly'
                          ? [
                              'Hour (UTC)',
                              'Function',
                              'Attempted',
                              'Success',
                              'Failure',
                              'Informational',
                            ]
                          : [
                              'Function',
                              'Attempted',
                              'Success',
                              'Failure',
                              'Informational',
                            ]
                        ).map((h) => (
                          <th
                            key={h}
                            scope="col"
                            className="text-left px-4 py-3 text-[14px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-r border-[#dad7d3] last:border-r-0"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.map((r, i) => (
                        <tr
                          key={`${r.hour ?? ''}:${r.eventName}:${i}`}
                          className="hover:bg-[#f9fafb]"
                        >
                          {report.granularity === 'hourly' && (
                            <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                              {r.hour ?? ''}
                            </td>
                          )}
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                            {r.eventName}
                          </td>
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                            {r.attempted}
                          </td>
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                            {r.success}
                          </td>
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                            {r.failure}
                          </td>
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-[#dad7d3]">
                            {r.informational ?? 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {reportMode === 'agent' && agentReport && (
            <>
              <p
                className="mb-3 font-['Lato','Arial',sans-serif] text-[14px] text-[#1d252c]"
                data-testid="agent-reporting-summary"
              >
                {visibleAgentEvents} action
                {visibleAgentEvents === 1 ? '' : 's'} across{' '}
                {visibleAgentRows.length} row
                {visibleAgentRows.length === 1 ? '' : 's'}, by{' '}
                {agentReport.by === 'tellerId' ? 'Teller ID' : 'Agent UPN'}
                {selectedAgent !== ALL_AGENTS ? ` (${selectedAgent})` : ''}.
              </p>
              {visibleAgentRows.length === 0 ? (
                <p
                  data-testid="agent-reporting-empty"
                  className="font-['Lato','Arial',sans-serif] text-[14px] text-[#6b7280]"
                >
                  No actions were recorded in this window.
                </p>
              ) : (
                <div className="border-2 border-[#dad7d3] bg-white overflow-x-auto">
                  <table
                    className="w-full border-collapse"
                    role="table"
                    aria-label="Per-agent action volume report"
                    data-testid="agent-reporting-table"
                  >
                    <thead>
                      <tr className="bg-[#f3f4f6]">
                        {(agentReport.granularity === 'hourly'
                          ? [
                              'Hour (UTC)',
                              agentReport.by === 'tellerId'
                                ? 'Teller ID'
                                : 'Agent UPN',
                              'Function',
                              'Attempted',
                              'Success',
                              'Failure',
                              'Informational',
                            ]
                          : [
                              agentReport.by === 'tellerId'
                                ? 'Teller ID'
                                : 'Agent UPN',
                              'Function',
                              'Attempted',
                              'Success',
                              'Failure',
                              'Informational',
                            ]
                        ).map((h) => (
                          <th
                            key={h}
                            scope="col"
                            className="text-left px-4 py-3 text-[14px] font-['Lato','Arial',sans-serif] font-bold text-[#1d252c] border-b-2 border-r border-[#dad7d3] last:border-r-0"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleAgentRows.map((r, i) => (
                        <tr
                          key={`${r.hour ?? ''}:${r.agent}:${r.eventName}:${i}`}
                          className="hover:bg-[#f9fafb]"
                        >
                          {agentReport.granularity === 'hourly' && (
                            <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                              {r.hour ?? ''}
                            </td>
                          )}
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                            {r.agent}
                          </td>
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                            {r.eventName}
                          </td>
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                            {r.attempted}
                          </td>
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                            {r.success}
                          </td>
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-r border-[#dad7d3]">
                            {r.failure}
                          </td>
                          <td className="px-4 py-2 text-[14px] font-['Lato','Arial',sans-serif] text-[#1d252c] border-b border-[#dad7d3]">
                            {r.informational ?? 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </main>
  );
}
